import { ArrowCircleDownIcon } from '@heroicons/react/solid';
import { Typography } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { BiUpload } from 'react-icons/bi';
import { useRecoilValue } from 'recoil';

import ButtonDefault, { ButtonVariants } from '../../shared/basic/button';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import { formateErrorObject } from '../../shared/formateErrorObject';
import { notify } from '../../shared/notify';
import BadgeCheckIcon from '../../../../assets/Icons/BadgeCheckIcon';
import DoubleTick from '../../../../assets/Icons/DoubleTick';
import TimerClockIcon from '../../../../assets/Icons/TimerClockIcon';
import { localUserID, user } from '../../../atoms/user';
import { addToCart, addToRecentlyViewed } from '../../../services/Cart';
import { AddToCartProps } from '../../../utils/interfaces/cart';
import {
  IHeroSectionCourseLandingPage,
  ISlidCourse,
} from '../../../utils/interfaces/landingPage';
import { User } from '../../../utils/interfaces/User';

interface ProductProps {
  openModal: (productId: number) => void;

  heroSectionData?: IHeroSectionCourseLandingPage;
  authorsData:
  | {
    follower_count: string;
    user_name: string;
  }
  | undefined;
  slidCourseContainerData?: ISlidCourse;
  token?: string;
  sectionRef?: any;
  openShare?: () => void;
  courseData?: any;
  landingPageData?: any;
  isPurchased?: boolean;
}

const HeroSectionCourse: React.FC<ProductProps> = ({
  heroSectionData,
  slidCourseContainerData,
  openShare,
  sectionRef,
  courseData,
  openModal,
  landingPageData,
  isPurchased = false,
}) => {
  const [loadingAddToCart, setLoadingAddToCart] = useState({
    value: false,
    id: 0,
  });
  const screens = useBreakpoint();
  const router = useRouter();
  const localUserId = useRecoilValue(localUserID);
  const userData: User | undefined = useRecoilValue(user);
  const handleAddDataToCart = async (
    payload: AddToCartProps,
    type: 'hardCopy' | 'digitalCopy',
  ) => {
    try {
      setLoadingAddToCart({ value: true, id: payload.id });

      await addToCart(payload);
      addToRecentlyViewed(payload.id, userData?.zl_uid);
      notify(
        `${slidCourseContainerData?.bookName} ${type === 'hardCopy' ? 'Hard Copy' : 'Digital Copy'}  added to cart successfully!`,
        'success',
      );
      router.push('/cart');
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setLoadingAddToCart({ value: false, id: payload.id });
    }
  };

  return (
    <React.Fragment>
      <div className='text-fadedWhite  flex flex-col gap-y-4 md:gap-y-6  lg:w-1/2 xl:w-3/5'>
        {/* <ShouldRender check={screens.lg}>
          <Breadcrumbs />
        </ShouldRender> */}
        <div className='flex  flex-col gap-y-4 md:gap-y-6 xl:mt-2'>
          <div className='flex flex-col gap-y-3 xl:gap-y-5'>
            <Typography.Title
              level={1}
              className='text-xl md:text-3xl lg:text-2xl xl:text-[32px] mb-0 font-semibold text-white'
            >
              {heroSectionData?.productTitle}
            </Typography.Title>
            <span className='text-sm md:text-base xl:text-lg font-medium text-[#FFFFFFCC] '>
              {heroSectionData?.shortDescription}
            </span>
          </div>
          <div className='flex flex-wrap gap-2'>
            {heroSectionData?.tags?.map((item, index) => (
              <div
                className='bg-white border-none px-3 2xl:px-4 2xl:py-2.5 py-1.5 md:py-2 text-xs 2xl:text-sm font-semibold rounded-[4px] md:rounded-[8px] uppercase text-black'
                key={`${item}-${index}`}
              >
                {item}
              </div>
            ))}
          </div>
        </div>

        <Typography.Title
          level={3}
          className='text-sm text-[#FFFFFF99] leading-5 '
        >
          Created by{' '}
          <Link
            href={`/author/${landingPageData?.form?.authorId}`}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex underline pl-1 text-white'
            style={{ textUnderlineOffset: '2px' }}
          >
            <p className='inline-flex underline pl-1 text-white'>
              {heroSectionData?.authorName?.toUpperCase()}
            </p>
          </Link>{' '}
        </Typography.Title>
        <ShouldRender check={!screens.lg && !isPurchased}>
          <ShouldRender
            check={courseData?.is_available && courseData?.i18n_attributes}
          >
            <div className=' flex flex-col gap-y-2'>
              <div className='flex items-start justify-between gap-5'>
                <div className='flex text-white gap-x-1 items-center'>
                  <BadgeCheckIcon />
                  <p className='text-xs'>
                    Premium content with one-time payment.
                  </p>
                </div>
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openShare && openShare();
                  }}
                  className='w-6 h-6 min-w-6 min-h-6 rounded-full flex items-center justify-center bg-grayZl-900 hover:bg-grayZl-800 cursor-pointer'
                >
                  <BiUpload className='w-[18px] h-[18px] text-white' />
                </div>
              </div>
              <div>
                <span className='flex xxxs:gap-x-1 md:gap-x-2 items-center pl-1'>
                  <p className='text-xl md:text-2xl font-semibold text-white whitespace-nowrap'>
                    {courseData?.currency_symbol} {courseData?.main_price}
                  </p>
                  <p className='line-through text-[#FFFFFF99] text-sm md:text-base font-medium whitespace-nowrap'>
                    {courseData?.currency_symbol} {courseData?.stroked_price}
                  </p>
                  {courseData?.stroked_price && courseData?.main_price ? (
                    <p className='hidden xxxs:flex gap-x-1 pl-1 md:pl-0 text-[#16A34A] font-bold text-sm md:text-base items-center'>
                      {Math.round(
                        ((courseData.stroked_price -
                          Number(courseData.main_price)) /
                          courseData.stroked_price) *
                        100,
                      )}
                      %{' '}
                      <ArrowCircleDownIcon className='w-4 h-4 md:w-5 md:h-5' />
                    </p>
                  ) : null}
                </span>
              </div>
              <div className='flex items-center text-xs md:text-sm text-[#EF4444]'>
                <TimerClockIcon />
                <span className='inline-flex px-1 font-bold'>2 days</span>
                <span className='translate-y-[1px] font-medium'>
                  {' '}
                  left at this price!
                </span>
              </div>
            </div>
          </ShouldRender>
          <div className='flex gap-y-3 flex-col'>
            <div ref={sectionRef} className='flex flex-col '>
              {isPurchased ? (
                <ButtonDefault
                  size={4}
                  rounded={false}
                  roundedSm={true}
                  enableScaling={false}
                  onClick={(e) => {
                    e.stopPropagation();
                    router.push('/library/?tab=3');
                  }}
                  variant={ButtonVariants.PRIMARY}
                  className='flex w-full justify-center z-20 px-5 py-3 font-medium text-xs whitespace-nowrap'
                >
                  <span className='px-4 text-sm md:text-base font-semibold py-1'>
                    Start Learning
                  </span>
                </ButtonDefault>
              ) : (
                <>
                  <ButtonDefault
                    size={4}
                    rounded={false}
                    roundedSm={true}
                    enableScaling={false}
                    available={
                      courseData?.is_available && courseData?.i18n_attributes
                    }
                    loading={
                      Number(slidCourseContainerData?.productId) ===
                      loadingAddToCart.id && loadingAddToCart.value
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddDataToCart(
                        {
                          id: Number(slidCourseContainerData?.productId) as number,
                          temp_user_id: localUserId,
                          quantity: 1,
                          trackingData: {
                            productData: {
                              price: Number(slidCourseContainerData?.main_price),
                              id: Number(slidCourseContainerData?.productId),
                              heading: slidCourseContainerData?.bookName as string,
                              quantity: 1 as number,
                              currency: courseData?.i18n_attributes?.currency,
                              category: landingPageData?.form?.tags?.[0],
                            },
                            userData,
                            pathname: router.pathname,
                            productType: 'course',
                            type: 'courses',
                          },
                        },
                        'hardCopy',
                      );
                    }}
                    variant={ButtonVariants.PRIMARY}
                    className='flex w-full justify-center z-20  px-5 py-3 font-medium text-xs whitespace-nowrap'
                  >
                    <span className='px-4 text-sm md:text-base font-semibold py-1'>
                      Buy Now
                    </span>
                  </ButtonDefault>
                  <span className='flex bg-[#340905] gap-x-1 -translate-y-1 pt-2 font-medium text-xs text-[#FF7837] justify-center items-center pb-0.5 rounded-b-[8px]'>
                    <DoubleTick /> Lifetime Access
                  </span>
                </>
              )}
            </div>

            {!isPurchased && (
              <ButtonDefault
                size={4}
                rounded={false}
                roundedSm={true}
                enableScaling={false}
                onClick={() => {
                  openModal(Number(slidCourseContainerData?.productId));
                }}
                variant={ButtonVariants.WHITE}
                className='flex w-full justify-center  px-5 py-3  border-none font-medium text-xs whitespace-nowrap'
              >
                <span className='px-4 text-sm md:text-base font-semibold py-1'>
                  Talk To Us
                </span>
              </ButtonDefault>
            )}
          </div>
        </ShouldRender>
      </div>
    </React.Fragment>
  );
};
export default HeroSectionCourse;
